'use client';

import { Img } from '@/components/builder2/utils/Img';
import React from 'react';
import { useNode, useEditor } from '@craftjs/core';
import { Button } from '@/components/ui/button';
import { Element } from '@craftjs/core';
import { isBlankHtml, navigateBuilderCta } from '@/utils/functions';
import { htmlSeguro } from '../../utils/htmlSeguro';
import { AreaDePiezas, Pieza } from '../../utils/piezaLibre';
import { useTranslation } from '@/i18n/hooks/useTranslation';

interface HeroMinimalisticProps {
  title?: string;
  subtitle?: string;
  buttonText1?: string;
  buttonText2?: string;
  buttonLink1?: string;
  buttonLink2?: string;
  bgColor?: string;
  textColor?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  buttonPaddingX?: string;
  buttonPaddingY?: string;
  carImageUrl?: string;
  titleAlignment?: 'left' | 'center' | 'right';
  children?: React.ReactNode;
}

export const HeroMinimalistic = ({
  title = 'Descubre Tu Próximo Vehículo',
  subtitle = 'Explora nuestra selección premium de autos y encuentra el que se adapta a tu estilo de vida',
  buttonText1 = 'Ver vehículos',
  buttonText2 = 'Contactar',
  buttonLink1 = '/vehicles',
  buttonLink2 = '/contact',
  bgColor = '#ffffff',
  textColor = '#333333',
  buttonBgColor = '#e05d31',
  buttonTextColor = '#ffffff',
  buttonPaddingX = '1.5rem',
  buttonPaddingY = '0.5rem',
  carImageUrl = 'https://images.unsplash.com/photo-1493238792000-8113da705763?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80',
  titleAlignment = 'left',
  children,
}: HeroMinimalisticProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
    id,
  } = useNode((state) => ({
    selected: state.events.selected,
    dragged: state.events.dragged,
  }));
  const { t } = useTranslation();

  // Detectar si estamos en modo editor
  const { isEnabled } = useEditor((state) => ({
    isEnabled: state.options.enabled,
  }));

  // Respeta el link del builder: navega si es ruta/URL, scroll si es #ancla.
  const scrollToSection = (sectionId: string) => {
    if (isEnabled) return; // No hacer nada en modo editor
    navigateBuilderCta(sectionId);
  };

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connect(el); }}
      style={{
        backgroundColor: bgColor,
        color: textColor,
        border: selected ? '2px dashed #666666' : '1px solid transparent',
        position: 'relative',
        transform: 'translateZ(0)',
      }}
      className='w-full py-16 md:py-24 overflow-hidden'
    >
      <AreaDePiezas className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='grid md:grid-cols-2 gap-12 lg:gap-16 items-center'>
          {/* Left side - Text content */}
          <div style={{ textAlign: titleAlignment }}>
            {/* Accent bar */}
            <Pieza clave="barra" etiqueta="Barra">
            <div
              className='rounded-full mb-6'
              style={{
                width: '4rem',
                height: '0.375rem',
                backgroundColor: buttonBgColor,
                marginLeft: titleAlignment === 'center' ? 'auto' : titleAlignment === 'right' ? 'auto' : '0',
                marginRight: titleAlignment === 'center' ? 'auto' : '0',
              }}
            />
            </Pieza>

            <Pieza clave="titulo" tipo="texto" etiqueta="Título">
            {!isBlankHtml(title) && (
              <h1
                className='text-5xl md:text-6xl font-black mb-6'
                style={{
                  color: textColor,
                  letterSpacing: '-0.025em',
                  lineHeight: '1.1',
                }}
                dangerouslySetInnerHTML={htmlSeguro(title || '')}
              />
            )}
            </Pieza>

            <Pieza clave="subtitulo" tipo="texto" etiqueta="Descripción">
            {!isBlankHtml(subtitle) && (
              <p
                className='text-lg md:text-xl mb-10 max-w-lg'
                style={{
                  color: textColor,
                  opacity: 0.8,
                  lineHeight: '1.7',
                  marginLeft: titleAlignment === 'center' ? 'auto' : titleAlignment === 'right' ? 'auto' : '0',
                  marginRight: titleAlignment === 'center' ? 'auto' : titleAlignment === 'left' ? 'auto' : '0',
                }}
                dangerouslySetInnerHTML={htmlSeguro(subtitle || '')}
              />
            )}
            </Pieza>

            <Pieza clave="botones" etiqueta="Botones">
            {(!isBlankHtml(buttonText1) || !isBlankHtml(buttonText2)) && (
              <div
                className='flex flex-wrap gap-4'
                style={{
                  justifyContent: titleAlignment === 'center' ? 'center' : titleAlignment === 'right' ? 'flex-end' : 'flex-start',
                }}
              >
                {!isBlankHtml(buttonText1) && (
                  <Button
                    className='rounded-full font-semibold text-base transition-all duration-300 hover:scale-105 hover:shadow-lg'
                    style={{
                      backgroundColor: buttonBgColor,
                      color: buttonTextColor,
                      paddingLeft: buttonPaddingX,
                      paddingRight: buttonPaddingX,
                      paddingTop: buttonPaddingY,
                      paddingBottom: buttonPaddingY,
                      boxShadow: `0 4px 14px 0 ${buttonBgColor}66`,
                    }}
                    onClick={() => scrollToSection(buttonLink1)}
                  >
                    <span dangerouslySetInnerHTML={htmlSeguro(buttonText1 || '')} />
                  </Button>
                )}
                {!isBlankHtml(buttonText2) && (
                  <Button
                    className='rounded-full font-semibold text-base border-2 bg-transparent transition-all duration-300 hover:scale-105'
                    variant='outline'
                    style={{
                      borderColor: buttonBgColor,
                      color: buttonBgColor,
                      paddingLeft: buttonPaddingX,
                      paddingRight: buttonPaddingX,
                      paddingTop: buttonPaddingY,
                      paddingBottom: buttonPaddingY,
                      backgroundColor: 'transparent',
                    }}
                    onClick={() => scrollToSection(buttonLink2)}
                  >
                    <span dangerouslySetInnerHTML={htmlSeguro(buttonText2 || '')} />
                  </Button>
                )}
              </div>
            )}
            </Pieza>
          </div>

          {/* Right side - Image with visual treatment */}
          <div className='flex justify-center md:justify-end'>
            <Pieza clave="imagen" tipo="imagen" etiqueta="Imagen">
            {carImageUrl && (
              <div className='relative' style={{ perspective: '1000px' }}>
                {/* Decorative gradient orb behind image */}
                <div
                  className='absolute -top-8 -right-8 w-72 h-72 rounded-full opacity-30 blur-3xl'
                  style={{ backgroundColor: buttonBgColor }}
                />
                {/* Secondary decorative orb */}
                <div
                  className='absolute -bottom-6 -left-6 w-48 h-48 rounded-full opacity-20 blur-2xl'
                  style={{ backgroundColor: buttonBgColor }}
                />

                {/* Dots pattern */}
                <div
                  className='absolute -top-4 -left-4 w-24 h-24 opacity-20'
                  style={{
                    backgroundImage: `radial-gradient(${buttonBgColor} 1.5px, transparent 1.5px)`,
                    backgroundSize: '10px 10px',
                  }}
                />

                {/* Image card */}
                <div
                  className='relative rounded-2xl overflow-hidden transition-transform duration-500 hover:scale-[1.02]'
                  style={{
                    boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)`,
                    borderBottom: `4px solid ${buttonBgColor}`,
                    transform: 'rotate(-1deg)',
                  }}
                >
                  <Img loading="eager" sizes="(max-width: 768px) 100vw, 50vw"
                    src={carImageUrl}
                    alt={t('site.hero.featuredCarAlt')}
                    className='w-full object-cover'
                    style={{
                      aspectRatio: '4 / 3',
                      maxHeight: '450px',
                      display: 'block',
                    }}
                  />
                  {/* Subtle gradient overlay on image */}
                  <div
                    className='absolute inset-0 pointer-events-none'
                    style={{
                      background: 'linear-gradient(135deg, rgba(0,0,0,0.03) 0%, transparent 50%, rgba(0,0,0,0.08) 100%)',
                    }}
                  />
                </div>

                {/* Small accent frame corner */}
                <div
                  className='absolute -bottom-3 -right-3 w-20 h-20 rounded-br-2xl border-b-2 border-r-2 opacity-40'
                  style={{ borderColor: buttonBgColor }}
                />
              </div>
            )}
            </Pieza>
          </div>
        </div>
        {children}
      </AreaDePiezas>
    </div>
  );
};

HeroMinimalistic.craft = {
  displayName: 'HeroMinimalistic',
  props: {
    title: 'Descubre Tu Próximo Vehículo',
    subtitle:
      'Explora nuestra selección premium de autos y encuentra el que se adapta a tu estilo de vida',
    buttonText1: 'Ver vehículos',
    buttonText2: 'Contactar',
    buttonLink1: '/vehicles',
    buttonLink2: '/contact',
    bgColor: '#ffffff',
    textColor: '#333333',
    buttonBgColor: '#e05d31',
    buttonTextColor: '#ffffff',
    buttonPaddingX: '1.5rem',
    buttonPaddingY: '0.5rem',
    carImageUrl:
      'https://images.unsplash.com/photo-1493238792000-8113da705763?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1000&q=80',
    titleAlignment: 'left',
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true,
  },
  isCanvas: true,
};
